import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/contexts/ToastContext";
import { useGettingStarted } from "./GettingStartedContext";

interface DemoPartner {
  initials: string;
  firstName: string;
  lastName: string;
  company: string;
  trade: string;
  location: string;
}

const DEMO_PARTNERS: DemoPartner[] = [
  {
    initials: "AD",
    firstName: "Alex",
    lastName: "Demo",
    company: "Example Restoration",
    trade: "Restoration",
    location: "Boise",
  },
  {
    initials: "RE",
    firstName: "Riley",
    lastName: "Example",
    company: "Sample Electric",
    trade: "Electrical",
    location: "Boise",
  },
  {
    initials: "CS",
    firstName: "Casey",
    lastName: "Sample",
    company: "Demo Plumbing",
    trade: "Plumbing",
    location: "Boise",
  },
];

/**
 * Fictional partner cards shown at the top of the Find New Partners tab
 * ONLY while the "partners" walkthrough track is active. These are never
 * real users — the Request buttons pop a "practice only" toast rather
 * than sending anything to the backend.
 */
export default function DemoPartnersStrip() {
  const { activeTrack } = useGettingStarted();
  const { showInfo } = useToast();

  if (activeTrack !== "partners") return null;

  return (
    <div className="mb-6 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
          Walkthrough Example
        </h3>
        <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">
          Practice only
        </Badge>
      </div>
      {DEMO_PARTNERS.map((partner, index) => (
        <Card
          key={partner.initials}
          className="bg-white border border-gray-200"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <Avatar className="w-10 h-10 border-2 border-gray-200">
                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-xs font-bold">
                    {partner.initials}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {partner.firstName} {partner.lastName}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {partner.company} · {partner.trade}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {partner.location} · Fictional match
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                data-tour-target={index === 0 ? "demo-partner-request" : undefined}
                className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0"
                onClick={() =>
                  showInfo(
                    "Practice only",
                    "No connection request was sent. This is a walkthrough example."
                  )
                }
              >
                Request
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
